import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, ClipboardList, CheckCircle, AlertCircle, Search, User } from 'lucide-react';
import { userProfileService } from '../../../../services/userProfileService';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import { ItemBinCountingRow, CreateCountingListRequest } from '../../../../types/countingListCreation';
import { UserProfileDto } from '../../../../types/userProfile';

interface CreateCountingListModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRows: ItemBinCountingRow[];
    onSuccess: () => void;
}

export const CreateCountingListModal: React.FC<CreateCountingListModalProps> = ({
    isOpen,
    onClose,
    selectedRows,
    onSuccess
}) => {
    const [description, setDescription] = useState('');
    const [selectedOperatorId, setSelectedOperatorId] = useState('');
    const [selectedOperatorName, setSelectedOperatorName] = useState('');
    const [profiles, setProfiles] = useState<UserProfileDto[]>([]);
    const [operatorSearch, setOperatorSearch] = useState('');
    const [showOperatorDropdown, setShowOperatorDropdown] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successInfo, setSuccessInfo] = useState<{ batchID: string; linesCreated: number } | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const operatorSearchRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Load profiles from Profile table
    useEffect(() => {
        const loadProfiles = async () => {
            try {
                const allProfiles = await userProfileService.getAllProfilesForCompany();
                setProfiles(allProfiles);
            } catch (err) {
                console.error('Error loading profiles:', err);
            }
        };
        if (isOpen) {
            loadProfiles();
        }
    }, [isOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowOperatorDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter profiles by search term (UserID, ProfileName, ProfileID, Operator)
    const filteredProfiles = useMemo(() => {
        if (!operatorSearch.trim()) return profiles;
        const search = operatorSearch.toLowerCase();
        return profiles.filter(p =>
            p.userID.toLowerCase().includes(search) ||
            (p.profileName && p.profileName.toLowerCase().includes(search)) ||
            p.profileID.toLowerCase().includes(search) ||
            (p.operator && p.operator.toLowerCase().includes(search))
        );
    }, [profiles, operatorSearch]);

    // Derive warehouse from selected rows
    const warehouseSet = new Set(selectedRows.map(r => r.warehouseID));
    const warehouses = Array.from(warehouseSet);
    const warehouse = warehouses[0] || '';

    // Unique item count
    const itemSet = new Set(selectedRows.map(r => r.itemNo));
    const uniqueItems = itemSet.size;

    const handleSelectOperator = (profile: UserProfileDto) => {
        setSelectedOperatorId(profile.userID);
        setSelectedOperatorName(profile.profileName
            ? `${profile.userID} - ${profile.profileName}`
            : profile.userID);
        setOperatorSearch('');
        setShowOperatorDropdown(false);
    };

    const handleClearOperator = () => {
        setSelectedOperatorId('');
        setSelectedOperatorName('');
        setOperatorSearch('');
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const request: CreateCountingListRequest = {
                description: description.trim() || undefined,
                operatorID: selectedOperatorId || undefined,
                warehouse,
                lines: selectedRows.map(row => ({
                    itemNo: row.itemNo,
                    binCode: row.binCode,
                    warehouseID: row.warehouseID,
                    expectedQuantity: row.binQuantity ?? 0
                }))
            };

            const result = await countingListCreationService.createCountingList(request);

            if (result.success) {
                setSuccessInfo({
                    batchID: result.batchID,
                    linesCreated: result.linesCreated
                });
            } else {
                setErrorMessage(result.errorMessage || 'Ukjent feil ved opprettelse');
            }
        } catch (err: any) {
            setErrorMessage(err.message || 'Feil ved opprettelse av telleliste');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-blue-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Opprett telleliste</h2>
                    </div>
                    <button
                        onClick={successInfo ? onSuccess : onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4 space-y-4">
                    {successInfo ? (
                        /* Success state */
                        <div className="flex flex-col items-center py-4 space-y-3">
                            <CheckCircle className="w-12 h-12 text-green-500" />
                            <h3 className="text-lg font-medium text-gray-900">Telleliste opprettet!</h3>
                            <div className="text-center text-sm text-gray-600 space-y-1">
                                <p>Batch-ID: <span className="font-mono font-medium">{successInfo.batchID}</span></p>
                                <p>{successInfo.linesCreated} tellelinjer opprettet</p>
                            </div>
                            <button
                                onClick={onSuccess}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Lukk
                            </button>
                        </div>
                    ) : (
                        /* Form state */
                        <>
                            {/* Error message */}
                            {errorMessage && (
                                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-red-700">{errorMessage}</span>
                                </div>
                            )}

                            {/* Summary */}
                            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                                <p><span className="font-medium">{selectedRows.length}</span> vare-/lokasjonslinjer valgt</p>
                                <p><span className="font-medium">{uniqueItems}</span> unike varer</p>
                                {warehouses.length > 1 && (
                                    <p className="text-amber-600">Merk: Valget inneholder {warehouses.length} ulike lagersted ({warehouses.join(', ')})</p>
                                )}
                                {warehouses.length === 1 && (
                                    <p>Lagersted: <span className="font-medium">{warehouse}</span></p>
                                )}
                            </div>

                            {/* Name field */}
                            <div>
                                <label htmlFor="cl-description" className="block text-sm font-medium text-gray-700 mb-1">
                                    Navn på telleliste (valgfritt)
                                </label>
                                <input
                                    id="cl-description"
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Autogenerert BatchID brukes om tomt"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                        bg-white text-gray-900 placeholder-gray-400"
                                />
                            </div>

                            {/* Operator assignment - searchable */}
                            <div ref={dropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tildel operatør (valgfritt)
                                </label>

                                {selectedOperatorId ? (
                                    /* Selected operator display */
                                    <div className="flex items-center justify-between px-3 py-2 border border-green-300 bg-green-50 rounded-lg text-sm">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-green-600" />
                                            <span className="text-gray-900 font-medium">{selectedOperatorName}</span>
                                        </div>
                                        <button
                                            onClick={handleClearOperator}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                            title="Fjern tildeling"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    /* Search input */
                                    <div className="relative">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                ref={operatorSearchRef}
                                                type="text"
                                                value={operatorSearch}
                                                onChange={(e) => {
                                                    setOperatorSearch(e.target.value);
                                                    setShowOperatorDropdown(true);
                                                }}
                                                onFocus={() => setShowOperatorDropdown(true)}
                                                placeholder="Søk etter operatør-ID eller navn..."
                                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm
                                                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                                    bg-white text-gray-900 placeholder-gray-400"
                                            />
                                        </div>

                                        {/* Dropdown results */}
                                        {showOperatorDropdown && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {/* "Not assigned" option */}
                                                <button
                                                    onClick={() => {
                                                        handleClearOperator();
                                                        setShowOperatorDropdown(false);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm text-gray-500 italic hover:bg-gray-50 border-b"
                                                >
                                                    Ikke tildelt (kun lagersjef/konsulent)
                                                </button>

                                                {filteredProfiles.length > 0 ? (
                                                    filteredProfiles.map(p => (
                                                        <button
                                                            key={p.profileID}
                                                            onClick={() => handleSelectOperator(p)}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center gap-2"
                                                        >
                                                            <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                            <span className="font-medium">{p.userID}</span>
                                                            {p.profileName && (
                                                                <span className="text-gray-500">- {p.profileName}</span>
                                                            )}
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-2 text-sm text-gray-400 italic">
                                                        Ingen operatører funnet
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                {!successInfo && (
                    <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg
                                hover:bg-gray-50 transition-colors"
                            disabled={isSubmitting}
                        >
                            Avbryt
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || selectedRows.length === 0}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg
                                hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                                flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Oppretter...
                                </>
                            ) : (
                                <>
                                    <ClipboardList className="w-4 h-4" />
                                    Opprett telleliste
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
